/**
 * @file edit-brand.tsx
 * @deprecated Copied to app/modules/procurement/pages/master/brands/edit-brand.tsx. This file still serves the existing /UProcure routes.
 * @module app/modules/uProcure/master/brands/edit-brand
 * @description Component for editing a brand in the master section of uProcure.
 *   Provides a form to update brand name and logo with validation and API integration.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 *
 * @todo Missing auditLogMessage: pass generateLogMessageForBrand via brandsJSON.editBrands
 *   ({ ..., auditLogMessage }) like uprocure-admin — extend apiJSON/master.ts first.
 * @todo isTobacco not saved: brandsJSON.editBrands only appends title/image; isTobacco from this
 *   form is passed in the call but dropped by apiJSON — fix brandsJSON.editBrands.
 * @todo Add useEffect guard when location state is missing (uprocure-admin calls history.back()).
 * @todo Remove debug console.log calls (brand, selectedOption) before merge.
 * @todo Remove duplicate h1 when PageTitle is used on the route wrapper.
 */

import clsx from "clsx";
import React, { FC, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import UploadImage from "../.../../../../../../_admin/assets/media/svg_uTrade/add-img.svg";
import APICallService from "../../../../../api/apiCallService";
import { master } from "../../../../../api/apiEndPoints";
import { brandsJSON } from "../../../../../api/apiJSON/master";
import { success } from "../../../../../Global/toast";
import { masterToast } from "../../../../../utils/toast";
import Close from "../../../../../_admin/assets/media/svg_uTrade/cross-rounded-gray.svg";
import { Brand } from "../../../../../utils/constants";
import { fileValidation } from "../../../../../Global/fileValidation";
import { useDebounce } from "../../../../../utils/useDebounce";
// import { warehouse as warehouseStoreString } from '../../../../utils/storeString';
// import { getKey } from '../../../../Global/history';
import { CreatableSelectWhite } from "../../../../custom/Select/CreatableSelectWhite";
import Method from "../../../../../utils/methods";
import { useAuth } from "../../../auth";
import { useWarehouse } from "../../../../context/WarehouseContext";
const EditBrand: FC = () => {
  const { currentUser } = useAuth();

  const { selectedWarehouse } = useWarehouse();
  const navigate = useNavigate();
  const { state }: any = useLocation();
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState<{
    brandName: string;
    brandImage: string;
    brandImageReader: any;
    isTobacco: boolean;
  }>({
    brandName: state.title,
    brandImage: state.image,
    brandImageReader: state.image,
    isTobacco: state?.isTobacco || false,
  });
  const [initialBrand, setInitialBrand] = useState<any>({
    brandName: state.title,
    brandImage: state.image,
    isTobacco: state?.isTobacco || false,
  });
  const [validation, setValidation] = useState<{
    brandName: boolean;
    brandImage: boolean;
  }>({
    brandName: false,
    brandImage: false,
  });
  const handleCategoryName = (value: any) => {
    let temp = { ...brand };
    let validateTemp = { ...validation };
    temp.brandName = value.trimStart();
    if (value.trimStart().length > 0) {
      validateTemp.brandName = false;
    } else {
      validateTemp.brandName = true;
    }
    setBrand(temp);
    setValidation(validateTemp);
  };
  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    let temp = { ...brand };
    let validateTemp = { ...validation };
    if (!selectedFiles || selectedFiles.length === 0) return;
    if (fileValidation(selectedFiles[0])) {
      validateTemp.brandImage = false;
      temp.brandImage = URL.createObjectURL(selectedFiles[0]);
      temp.brandImageReader = selectedFiles[0];
      setBrand(temp);
    } else {
      event.target.value = "";
    }
    setValidation(validateTemp);
  };
  const handleImageDiscard = () => {
    let temp = { ...brand };
    let validateTemp = { ...validation };
    validateTemp.brandImage = true;
    temp.brandImage = "";
    temp.brandImageReader = {};
    setBrand(temp);
    setValidation(validateTemp);
  };
  const handleCheckBox = (value: boolean) => {
    let temp = { ...brand };
    temp.isTobacco = value;
    setBrand(temp);
  };
  const handleSaveCategory = async () => {
    let temp = { ...brand };
    let validateTemp = { ...validation };
    if (!temp.brandName && !temp.brandImage) {
      validateTemp.brandName = true;
      validateTemp.brandImage = true;
    } else if (!temp.brandName) {
      validateTemp.brandName = true;
    } else if (!temp.brandImage) {
      validateTemp.brandImage = true;
    } else {
      setLoading(true);
      let apiService = new APICallService(
        master.editBrands,
        brandsJSON.editBrands({
          title: brand.brandName.trim(),
          image: brand.brandImageReader,
          isTobacco: brand.isTobacco,
        }),
        { _id: state._id },
        "",
        "",
        "",
        Brand,
        selectedWarehouse?.value || null,
      );
      let response = await apiService.callAPI();
      if (response) {
        success(masterToast.editbrand);
        const data = Method.compareComplexObjects(initialBrand, brand);
        navigate("/UProcure/master/brands");
      }
    }
    setValidation(validateTemp);
    setLoading(false);
  };
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [selectedOption, setSelectedOption] = useState<any>(
    state.title ? { value: state.title, label: state.title } : null,
  );
  const fetchBrands = async (searchText = "") => {
    setLoading(true);
    let params: any = {
      sortKey: "title",
      sortOrder: 1,
      searchTerm: searchText,
    };
    let apiService = new APICallService(
      master.listBrands,
      brandsJSON.listBrands(params),
      "",
      "",
      "",
      "",
      Brand,
      selectedWarehouse?.value || null,
    );
    let response = await apiService.callAPI();
    if (response.records) {
      const formattedOptions = response.records.map((brand: any) => ({
        value: brand._id,
        label: brand.title,
      }));
      setOptions(formattedOptions);
    } else {
      setOptions([]);
    }
    setLoading(false);
  };

  const debounce = useDebounce(fetchBrands, 500);

  const handleInputChange = (value: any) => {
    setInputValue(value);

    if (value.length > 0) {
      debounce(value);
      setBrand((prevState) => ({
        ...prevState,
        brandName: value.trimStart(),
      }));
      setValidation((prevState) => ({
        ...prevState,
        brandName: value.trim().length === 0,
      }));
    } else {
      setOptions([]);
    }
  };

  const handleBlur = () => {
    if (brand.brandName.trim().length === 0) {
      setValidation((prevState) => ({
        ...prevState,
        brandName: true,
      }));
    } else {
      setValidation((prevState) => ({
        ...prevState,
        brandName: false,
      }));
    }
  };

  const handleChange = (newValue: any) => {
    let isValid = true;
    if (newValue) {
      if (newValue.__isNew__) {
        setBrand((prev) => ({
          ...prev,
          brandName: newValue.label.trimStart(),
        }));
      } else {
        setBrand((prev) => ({
          ...prev,
          brandName: newValue.label.trimStart(),
        }));
      }
      isValid = true;
    } else {
      setBrand((prev) => ({
        ...prev,
        brandName: "",
      }));
      isValid = false;
    }
    setValidation((prev) => ({
      ...prev,
      brandName: !isValid,
    }));
    setSelectedOption(newValue);
  };
  console.log("brand", brand);
  console.log("selectedOption", selectedOption);
  return (
    <div className="p-5">
      <Row>
        <Col lg={12} className="mb-6">
          <h1 className="fs-22 fw-bolder">Edit brand</h1>
        </Col>
        <Col xs={12}>
          <Card className="border bg-light position-relative mb-6">
            <Card.Body className="p-5">
              <Row>
                <Col lg={6} className="mb-6">
                  <Row className="align-items-center">
                    <Col lg={4}>
                      <label className="fs-16 fw-500 text-dark mb-lg-0 mb-3">
                        Brand name
                      </label>
                    </Col>
                    <Col lg={8}>
                      {/* <input
                        type="text"
                        className={clsx(
                          'form-control form-control-solid bg-white h-60px border form-control-lg text-dark fs-16 fw-600',
                          validation.brandName ? 'border-danger' : ''
                        )}
                        name="name"
                        placeholder="Type here…"
                        value={brand.brandName}
                        disabled={loading}
                        onChange={(
                          e: React.ChangeEvent<HTMLInputElement>
                        ): void => handleCategoryName(e.target.value)}
                      /> */}
                      <CreatableSelectWhite
                        isClearable
                        isLoading={loading}
                        options={options}
                        value={
                          options.find(
                            (option: any) => option.label === brand.brandName,
                          ) ||
                          (brand.brandName
                            ? { label: brand.brandName, value: brand.brandName }
                            : null)
                        }
                        onChange={handleChange}
                        inputValue={inputValue}
                        onInputChange={(value: any) => handleInputChange(value)}
                        placeholder="Type to search or create..."
                        border={clsx(validation.brandName ? "#e55451" : "")}
                        onBlur={() => handleBlur()}
                      />
                      <div className="fv-plugins-message-container">
                        <span className="text-danger fs-12 fw-bold">
                          {validation.brandName
                            ? "Please enter brand name"
                            : ""}
                        </span>
                      </div>
                    </Col>
                  </Row>
                </Col>
                <Col lg={6} className="mb-6">
                  <Row className="align-items-center">
                    <Col lg={4}>
                      <div className="fs-16 fw-500 text-dark mb-lg-0 mb-3">
                        Is the brand related to tobacco ?
                      </div>
                    </Col>
                    <Col lg={8} className="d-flex align-items-center">
                      <div className="d-flex align-items-center me-4">
                        <span>
                          <input
                            className="form-check-input pt-1 h-25px w-25px  me-1"
                            type="radio"
                            name={"isTobaccoNo"}
                            id={"isTobaccoNo"}
                            checked={!brand.isTobacco}
                            onChange={(): void => handleCheckBox(false)}
                          />
                        </span>
                        <label
                          className="form-check-label fs-16 fw-500 text-dark ms-2"
                          htmlFor={"isTobaccoNo"}
                        >
                          {"No"}
                        </label>
                      </div>
                      <div className="d-flex align-items-center">
                        <span>
                          <input
                            className="form-check-input pt-1 h-25px w-25px  me-1"
                            type="radio"
                            name={"isTobaccoYes"}
                            id={"isTobaccoYes"}
                            checked={brand.isTobacco}
                            onChange={(): void => handleCheckBox(true)}
                          />
                        </span>
                        <label
                          className="form-check-label fs-16 fw-500 text-dark ms-2"
                          htmlFor={"isTobaccoYes"}
                        >
                          {"Yes"}
                        </label>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row>
                <Col lg={2}>
                  <label className="fs-16 fw-500 text-dark mb-lg-0 mb-3 mt-lg-3">
                    Upload <br />
                    brand logo
                  </label>
                </Col>
                <Col lg={10} className="d-flex">
                  <div className="d-flex flex-column">
                    <div className="upload-btn-wrapper">
                      <div className="symbol symbol-160px symbol-md-170px">
                        <img
                          className={clsx(
                            "border bg-white object-fit-contain",
                            validation.brandImage ? "border-danger" : "border",
                          )}
                          src={
                            brand.brandImage ? brand.brandImage : UploadImage
                          }
                          alt=""
                        />
                      </div>
                      <input
                        className="w-100 h-100"
                        type="file"
                        name="myfile"
                        onChange={(
                          e: React.ChangeEvent<HTMLInputElement>,
                        ): void => handleImageChange(e)}
                      />
                      <div className="fv-plugins-message-container">
                        <span className="text-danger fs-12 fw-bold">
                          {validation.brandImage
                            ? "Please upload brand logo"
                            : ""}
                        </span>
                      </div>
                    </div>
                    <span className="text-gray text-italic">{`Image dimensions:-(700x700px)`}</span>
                  </div>
                  {brand.brandImage && !loading ? (
                    <label
                      className="btn btn-icon btn-circle btn-active-color-primary w-50px h-50px responsive-closed"
                      data-kt-image-input-action="change"
                      title=""
                    >
                      <img
                        src={Close}
                        onClick={(): void => handleImageDiscard()}
                        className="responsive-closed-icon-closed"
                        alt=""
                        height={33}
                        width={33}
                      />
                    </label>
                  ) : (
                    <></>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row className="justify-content-end justify-content-md-start g-3">
        <Col xs="auto">
          <Button
            variant="danger"
            size="lg"
            className="uw-btn uw-btn-danger uw-btn-lg btn-active-danger"
            onClick={() => navigate("/UProcure/master/brands")}
          >
            {" "}
            Cancel
          </Button>
        </Col>
        <Col xs="auto">
          <Button className="uw-btn uw-btn-primary uw-btn-lg"
            variant="primary"
            size="lg"
            onClick={handleSaveCategory}
            disabled={loading}
          >
            {!loading && <span className="indicator-label">Save Changes</span>}
            {loading && (
              <span className="indicator-progress" style={{ display: "block" }}>
                Please wait...
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            )}
          </Button>
        </Col>
      </Row>
    </div>
  );
};
export default EditBrand;
